/* eslint-disable @typescript-eslint/no-unused-vars */
import { api, showErrorNotification, showSuccessNotification } from '@med-fe/util';
import { call, put } from 'redux-saga/effects';
import { fetchUnplannedLocationsSuccess, fetchUnplannedLocationsFailure } from './actions';
import {
  acceptHooAccommodationFailure,
  acceptHooAccommodationSuccess,
  deleteHooAccommodationFailure,
  deleteHooAccommodationSuccess,
  fetchLocationsForRequestorFailure,
  fetchLocationsForRequestorSuccess,
  fetchRequestorsFailure,
  fetchRequestorsSuccess,
  fetchRequestReasonsSuccess,
  fetchRequestReasonsFailure,
  fetchRequestTypesSuccess,
  fetchRequestTypesFailure,
  rejectHooAccommodationFailure,
  rejectHooAccommodationSuccess,
  SaveHooRequestFailed,
  SaveHooRequestSuccess,
  searchHoo,
  searchHooFailed,
  searchHooSuccess,
  exportHooRequestsSuccess,
  exportHooRequestsFailure,
} from './actions';

export function* searchHooStartWorker({ payload }): Generator {
  try {
    const url = `/schedulechangerequests/search`;
    const data = yield call(api.post, url, payload, null, true, true);
    yield put(searchHooSuccess(data));
  } catch (e) {
    yield put(searchHooFailed(e));
    showErrorNotification();
  }
}

// Fetch requestors by job positions
export function* fetchRequestorsStartWorker(action: any) {
  try {
    const { positions } = action.payload;
    const url = `/employee/by-job-positions?positions=${positions}`;
    const response = yield call(api.get, url, true, true);
    // Unique requestors by ContactSid
    const uniqueRequestors = Array.from(new Map(response.map((item: any) => [item.ContactSid, item])).values()).map(
      (item: any) => ({
        label: item.ContactName,
        value: item.ContactSid,
        contact: item,
      })
    );
    yield put(fetchRequestorsSuccess(uniqueRequestors, response));
  } catch (error) {
    yield put(fetchRequestorsFailure(error));
  }
}

// Fetch locations for selected requestor
export function* fetchLocationsForRequestorStartWorker(action: any) {
  try {
    const { contactSid, allData, showAllLocations } = action.payload;
    const hasValidLocation = (item: any) => item.LocationSid && item.LocationNumber && item.LocationName;
    let locations;
    if (showAllLocations) {
      // For AV, RM, or MOD Admin: show all unique locations
      const uniqueLocations = Array.from(
        new Map(allData.filter(hasValidLocation).map((item: any) => [item.LocationSid, item])).values()
      );
      locations = uniqueLocations.map((item: any) => ({
        label: `${item.LocationNumber} - ${item.LocationName}`,
        value: item.LocationSid,
      }));
    } else {
      // For DM/AM: filter locations for selected contactSid
      locations = allData
        .filter((item: any) => item.ContactSid === contactSid && hasValidLocation(item))
        .map((item: any) => ({
          label: `${item.LocationNumber} - ${item.LocationName}`,
          value: item.LocationSid,
        }));
    }
    yield put(fetchLocationsForRequestorSuccess(locations));
  } catch (error) {
    yield put(fetchLocationsForRequestorFailure(error));
  }
}

export function* deleteHooAccommodationStartWorker({ payload }: any): Generator {
  const { requestId, search } = payload;
  try {
    const url = `/schedule-change-requests/${requestId}`;
    const data = yield call(api.put, url, null, null, true, true);
    yield put(searchHoo(search));
    yield put(deleteHooAccommodationSuccess(data));
    let deleteMessage =
      search.scheduleType === 'UnplannedHoo'
        ? 'Temporary hours request canceled successfully.'
        : 'Permanent hours request canceled successfully.';
    showSuccessNotification(data || deleteMessage);
  } catch (e: any) {
    yield put(deleteHooAccommodationFailure(e));
    // Extract message safely from Axios error response
    const message =
      e?.response?.data?.error?.messages?.[0] ||
      `Error while deleting ${
        search.scheduleType === 'UnplannedHoo' ? 'unplanned hoo' : 'hoo'
      } accommodation request. Please try again.`;
    showErrorNotification(message);
  }
}

export function* acceptHooAccommodationStartWorker({ payload }: any): Generator {
  const { requestId, search } = payload;
  try {
    const url = `/approve-schedule-change-requests/${requestId}`;
    const data = yield call(api.put, url, null, null, true, true);
    yield put(searchHoo(search));
    yield put(acceptHooAccommodationSuccess(data));
    let approveMessage =
      search.scheduleType === 'UnplannedHoo'
        ? 'Temporary hours request approved successfully.'
        : 'Permanent hours request approved successfully.';
    showSuccessNotification(data || approveMessage);
  } catch (e: any) {
    yield put(acceptHooAccommodationFailure(e));

    // Extract message safely from Axios error response
    const message =
      e?.response?.data?.error?.messages?.[0] ||
      `Error while accepting ${
        search.scheduleType === 'UnplannedHoo' ? 'unplanned hoo' : 'hoo'
      } accommodation request. Please try again.`;
    showErrorNotification(message);
  }
}

export function* rejectHooAccommodationStartWorker({ payload }: any): Generator {
  const { requestId, search, rejectReason } = payload;
  try {
    const url = `/reject-schedule-change-requests/${requestId}`;
    const data = yield call(api.put, url, { RejectReason: rejectReason }, null, true, true);
    yield put(searchHoo(search));
    yield put(rejectHooAccommodationSuccess(data));
    let rejectMessage =
      search.scheduleType === 'UnplannedHoo'
        ? 'Temporary hours request rejected successfully.'
        : 'Permanent hours request rejected successfully.';
    showSuccessNotification(data || rejectMessage);
  } catch (e: any) {
    yield put(rejectHooAccommodationFailure(e));
    // Extract message safely from Axios error response
    const message =
      e?.response?.data?.error?.messages?.[0] ||
      `Error while rejecting ${
        search.scheduleType === 'UnplannedHoo' ? 'unplanned hoo' : 'hoo'
      } accommodation request. Please try again.`;
    showErrorNotification(message);
  }
}

export function* saveHooAccommodationStartWorker({ payload }): Generator {
  const { onSuccess, onError, ...dataPayload } = payload;
  try {
    const url = `/schedule-change-requests`;
    // Build a sanitized body to guarantee expected fields are sent to backend
    const body = {
      ScheduleChangeRequestId: dataPayload.ScheduleChangeRequestId || null,
      RequestorId: dataPayload.RequestorId || null,
      LocationIds: dataPayload.LocationIds || [],
      EffectiveDate: dataPayload.EffectiveDate || null,
      EndDate: dataPayload.EndDate || null,
      RequestType: dataPayload.RequestType || null,
      Reason: dataPayload.Reason || null,
      Notes: dataPayload.Notes || null,
      AdditionalNotes: dataPayload.AdditionalNotes || null,
      Details: dataPayload.Details || [],
      ScheduleType: dataPayload.ScheduleType || dataPayload.HooType || 'StandardHoo',
    };
    const data = yield call(api.post, url, body, null, true, true);
    yield put(SaveHooRequestSuccess(data));
    showSuccessNotification(
      body.ScheduleType == 'StandardHoo'
        ? 'Permanent hours request saved successfully!'
        : 'Temporary hours request saved successfully!'
    );
    if (onSuccess) {
      onSuccess(data);
    }
  } catch (e: any) {
    yield put(SaveHooRequestFailed(e));
    if (onError) {
      onError(e);
    } else {
      // Extract message safely from Axios error response
      const message =
        e?.response?.data?.error?.messages?.[0] || 'Failed to save Permanent hours request. Please try again.';
      showErrorNotification(message);
    }
  }
}

// Fetch all locations (numbers) for UnPlanned HOO dropdown
export function* fetchUnplannedLocationsStartWorker(action: any): Generator {
  try {
    const positions = action?.payload?.positions || '';
    const activeOnly = Boolean(action?.payload?.activeOnly);
    const url = `/locations/numbers?pageNumber=0&pageSize=10000${positions ? `&positions=${positions}` : ''}${
      activeOnly ? '&activeOnly=true' : ''
    }`;
    const data: any = yield call(api.get, url);
    let items: any[] = [];
    if (Array.isArray(data)) items = data;
    else if (data && data.items) items = data.items;

    const mapped = items.map((x: any) => ({
      value: x.id ?? x.Id ?? x.locationId ?? x.LocationSid,
      label: `${x.number ?? x.Number ?? x.LocationNumber} - ${x.name ?? x.Name ?? x.LocationName}`,
    }));
    yield put(fetchUnplannedLocationsSuccess(mapped));
  } catch (e: any) {
    yield put(fetchUnplannedLocationsFailure(e));
    console.error('Failed to fetch unplanned locations', e);
  }
}

// Fetch request reasons by schedule type (StandardHoo or UnPlannedHoo)
export function* fetchRequestReasonsStartWorker(action: any): Generator {
  try {
    const { scheduleType } = action.payload;
    const url = `/schedulechangerequests/requestreasons?scheduleType=${scheduleType}`;
    const data: any = yield call(api.get, url, true, true);
    const reasons = (Array.isArray(data) ? data : [])
      .filter((item: any) => item.IsActive)
      .map((item: any) => ({
        label: item.Name,
        value: item.Name,
      }));
    yield put(fetchRequestReasonsSuccess(reasons));
  } catch (e: any) {
    yield put(fetchRequestReasonsFailure(e));
    console.error('Failed to fetch request reasons', e);
  }
}

// Fetch request types for UnPlanned HOO
export function* fetchRequestTypesStartWorker(action: any): Generator {
  try {
    const { scheduleType } = action.payload;
    const url = `/schedulechangerequests/requesttypes?scheduleType=${scheduleType}`;
    const data: any = yield call(api.get, url, true, true);
    const requestTypes = (Array.isArray(data) ? data : [])
      .filter((item: any) => item.IsActive)
      .map((item: any) => ({
        label: item.Name,
        value: item.Name,
      }));
    yield put(fetchRequestTypesSuccess(requestTypes));
  } catch (e: any) {
    yield put(fetchRequestTypesFailure(e));
    console.error('Failed to fetch request types', e);
  }
}

// Export HOO requests
export function* exportHooRequestsStartWorker({ payload }: any): Generator {
  try {
    const maxSize = 100000;
    const url = `/schedulechangerequests/export`;
    yield call(api.downloadFile, url, {
      ...payload,
      page: 0,
      size: maxSize,
    });
    yield put(exportHooRequestsSuccess());
  } catch (e: any) {
    yield put(exportHooRequestsFailure(e));
    showErrorNotification('Failed to export HOO requests. Please try again.');
  }
}
